import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { FontWeights, TextField, Tooltip } from "@flat-ui";
import { changeName } from "@faro-lotv/project-source";
import { createMutationSetElementName } from "@faro-lotv/service-wires";
import { Box, InputAdornment } from "@mui-wrapper";
import { MarkupDetails } from "@pages/project-details/project-markups/markup-details";
import { useAppDispatch } from "@store/store-helper";
import { noInputUnderlineSx, withEllipsis, withEllipsisFourLines } from "@styles/common-styles";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getBottomBorderWithBoxShadow } from "@utils/ui-utils";
import { useEffect, useState } from "react";
import { EditColumnValueIcon } from "@components/table/edit-column-value-icon";
import { PALETTE_VARS } from "@styles/palette-vars";

/** Time in ms to wait between hover and open the popover */
const ENTER_DELAY = 600;

/** Maximum length of markup name  */
const MARKUP_NAME_MAX_LENGTH = 200;

/**
 * The surface of this tooltip is the markup details card itself, so it drops everything flat-ui
 * gives a plain tooltip: the dark background, the border, the padding and the width cap. The
 * margin needs `!important` because flat-ui sets its placement margins through a more specific
 * selector.
 */
const MARKUP_DETAILS_TOOLTIP_SX = {
  backgroundColor: "background.paper",
  border: "none",
  padding: "0",
  margin: "0 !important",
  maxWidth: "none",
  color: "text.primary",
  fontSize: "14px"
};

/** Class name of annotation name edit icon, Used to control the visibility on hover */
const ANNOTATION_NAME_EDIT_ICON_CLASS = "annotationNameEdit";

/** Component that renders the markup name and an additional popover for the markup details */
export function MarkupNameAndDetails({
  markup,
  isSidePanelOpen = false
}) {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    projectId,
    hasPermissionToEditMarkup
  } = useMarkupContext();
  const projectApiClient = useProjectApiClient({
    projectId
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [markupName, setMarkupName] = useState(markup.name);
  const [isLoading, setIsLoading] = useState(false);

  /** Triggers when user clicks on check mark after update */
  async function updateName() {
    setIsLoading(true);
    setIsEditMode(false);

    // Early return after reset the name if markup name has empty value
    if (!markupName.trim().length) {
      setMarkupName(markup.name);
      setIsLoading(false);
      return;
    }

    // Early return if the markup name is not changed
    if (markupName === markup.name) {
      setIsLoading(false);
      return;
    }
    const mutations = [createMutationSetElementName(markup.id, markupName)];
    try {
      await projectApiClient.applyMutations(mutations);
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "name",
          isValueEmpty: false
        }
      });

      // Update local store
      await dispatch(changeName({
        id: markup.id,
        name: markupName.trim()
      }));
    } catch (error) {
      setMarkupName(markup.name);
      handleErrorWithToast({
        id: `updateAnnotationName-${Date.now().toString()}`,
        title: "Could not change annotation name. Please try again",
        error
      });
    } finally {
      setIsLoading(false);
    }
  }
  let EndIcon = null;
  if (isLoading) {
    EndIcon = <FaroButtonSpinner />;
  } else if (!isEditMode && hasPermissionToEditMarkup && !isSidePanelOpen) {
    EndIcon = <EditColumnValueIcon iconButtonProps={{
      className: ANNOTATION_NAME_EDIT_ICON_CLASS
    }} onClick={() => setIsEditMode(true)} />;
  }
  /**
   * This hook sets the state variable markupName to the value of markup.name whenever the markup object changes.
   * This ensures that the markupName state is kept in sync with the markup object, allowing updates to
   * be reflected in multiple instances of the component
   */
  useEffect(() => {
    setMarkupName(markup.name);
  }, [markup]);
  return <Box sx={{
    width: "100%",
    height: "92%",
    boxShadow: isEditMode ? getBottomBorderWithBoxShadow({
      thickness: "2px"
    }) : "none",
    ...(!isEditMode && {
      "&:hover": {
        [`& .${ANNOTATION_NAME_EDIT_ICON_CLASS}`]: {
          visibility: "visible"
        }
      }
    })
  }}>
      <Box component="div" onClick={() => {
      if (!isSidePanelOpen) {
        return;
      }
      setIsEditMode(true);
    }} sx={{
      cursor: "pointer",
      height: "100%",
      padding: !isSidePanelOpen ? "8px 10px" : "0"
    }}>
        <Box component="div" sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        borderBottom: isSidePanelOpen ? "1px solid transparent" : "none",
        ...(!isEditMode && {
          "&:hover": {
            borderBottom: isSidePanelOpen ? `1px solid ${PALETTE_VARS.text.primary}` : "none"
          }
        })
      }}>
          {isEditMode && <TextField autoFocus variant="standard" text={markupName} type={"text"} onKeyDown={event => {
          if (event.key === "Enter") {
            void updateName();
          }
          if (event.key === "Escape") {
            setMarkupName(markup.name);
            setIsEditMode(false);
          }
          if (!isSidePanelOpen) {
            event.stopPropagation();
          }
        }} onTextChanged={setMarkupName} onBlur={updateName} inputProps={{
          maxLength: MARKUP_NAME_MAX_LENGTH
        }} endAdornment={<InputAdornment position="end" sx={{
          color: "text.secondary",
          fontWeight: FontWeights.Regular
        }}>
                  {markupName.length}/{MARKUP_NAME_MAX_LENGTH}
                </InputAdornment>} controlStyle={{
          height: isSidePanelOpen ? "30px" : "auto",
          display: isSidePanelOpen ? "grid" : "inline-flex",
          width: "100%"
        }} sx={{
          fontSize: "12px",
          fontWeight: FontWeights.Bold,
          ...noInputUnderlineSx,
          "& .MuiInputBase-input": {
            padding: "0px",
            color: "text.primary"
          }
        }} />}

          {!isEditMode && (!isSidePanelOpen ? <Tooltip enterDelay={ENTER_DELAY} enterNextDelay={ENTER_DELAY} disableFocusListener title={<MarkupDetails markup={markup} />} onClose={() => setIsOpen(false)} onOpen={() => setIsOpen(true)} tooltipSx={MARKUP_DETAILS_TOOLTIP_SX}
        // flat-ui anchors the popper to a `span` it wraps the children in, which becomes
        // the flex item the `var` below used to be, so the truncation moves with it.
        sx={{
          ...withEllipsis,
          display: "block"
        }}>
                <Box component="var" sx={{
            ...withEllipsis,
            fontWeight: FontWeights.Bold,
            color: isOpen ? "primary.main" : "text.primary"
          }}>
                  {markupName ?? "-"}
                </Box>
              </Tooltip> : <Box component="var" sx={{
          ...withEllipsisFourLines,
          fontWeight: FontWeights.Bold,
          color: "text.primary"
        }}>
                {markupName ?? "-"}
              </Box>)}

          <Box sx={{
          marginLeft: "auto"
        }}>{EndIcon}</Box>
        </Box>
      </Box>
    </Box>;
}